import React, { useMemo, useState } from 'react';
import type { WizardState, ProtocolRecord } from '../types';
import type { Action } from '../state/configReducer';
import ProtocolEditor from '../components/ProtocolEditor';
import { getProtocolById } from '../state/configReducer';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
  hospital: string;
  setNotification: (n: { kind: 'success' | 'error' | 'info'; text: string } | null) => void;
}

function generateUniqueProtocolId(name: string, existing: Set<string>): string {
  // Latin transliteration not attempted here — Hebrew names produce a non-ascii
  // base; the admin can edit the slug afterwards. We just guarantee uniqueness.
  const base = (name || 'protocol')
    .toLowerCase()
    .replace(/[^a-z0-9א-ת]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'protocol';
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

const StepProtocols: React.FC<Props> = ({ state, dispatch, hospital, setNotification }) => {
  const { protocols } = state.data;
  const search = state.ui.protocolSearch;
  const selectedId = state.ui.selectedProtocolId;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = protocols.filter(p => {
      if (!term) return true;
      return [p.name, p.id, p.section, p.slug || '']
        .some(field => field.toLowerCase().includes(term));
    });

    const groups = new Map<string, ProtocolRecord[]>();
    for (const p of filtered) {
      const key = p.section || '(ללא קטגוריה)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    return Array.from(groups.entries());
  }, [protocols, search]);

  const onAddProtocol = () => {
    const existing = new Set(protocols.map(p => p.id));
    const name = '';
    const id = generateUniqueProtocolId(name, existing);
    const newProtocol: ProtocolRecord = {
      id,
      slug: id,
      name: '',
      section: protocols[0]?.section || '',
      attachments: [],
      hidden: false
    };
    dispatch({ type: 'ADD_PROTOCOL', protocol: newProtocol });
    dispatch({ type: 'SELECT_PROTOCOL', id });
  };

  const selected = selectedId ? getProtocolById(state.data, selectedId) : undefined;

  const onDeleteProtocol = () => {
    if (!selected) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteProtocol = () => {
    if (!selected) return;
    dispatch({ type: 'DELETE_PROTOCOL', id: selected.id });
    setDeleteConfirmOpen(false);
  };

  return (
    <div className="resus-admin__master-detail">
      <aside className="resus-admin__list-pane" aria-label="רשימת פרוטוקולים">
        <div className="resus-admin__list-header">
          <input
            className="resus-admin__search"
            type="search"
            placeholder="חיפוש פרוטוקול..."
            value={search}
            onChange={(e) => dispatch({ type: 'SET_PROTOCOL_SEARCH', value: e.target.value })}
          />
          <button type="button" className="resus-admin__btn resus-admin__btn--accent" onClick={onAddProtocol}>
            + פרוטוקול חדש
          </button>
        </div>

        {grouped.length === 0 && (
          <p className="resus-admin__subtle">לא נמצאו פרוטוקולים תואמים לחיפוש.</p>
        )}

        {grouped.map(([section, items]) => (
          <section key={section}>
            <div className="resus-admin__list-section-header">{section}</div>
            <ul className="resus-admin__list">
              {items.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`resus-admin__list-row${p.id === selectedId ? ' resus-admin__list-row--selected' : ''}`}
                    onClick={() => dispatch({ type: 'SELECT_PROTOCOL', id: p.id })}
                    style={{ width: '100%', border: 'none' }}
                  >
                    <div className="resus-admin__list-row__main">
                      <span className="resus-admin__list-row__name">
                        {p.name || <em className="resus-admin__subtle">(ללא שם)</em>}
                      </span>
                      <span className="resus-admin__list-row__sub">
                        {p.attachments.length > 0 && `${p.attachments.length} קבצים`}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </aside>

      <div className="resus-admin__detail-pane">
        {!selected && (
          <p className="resus-admin__subtle">בחרו פרוטוקול מהרשימה או הוסיפו חדש.</p>
        )}
        {selected && (
          <ProtocolEditor
            protocol={selected}
            state={state}
            hospital={hospital}
            dispatch={dispatch}
            onError={(msg) => setNotification({ kind: 'error', text: msg })}
            onDeleteProtocol={onDeleteProtocol}
          />
        )}
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteProtocol}
        title={`למחוק את הפרוטוקול "${selected?.name || ''}"? פעולה זו תסיר גם את כל ההקצאות לתרופות.`}
        showError={false}
        errorMessage=""
        onCloseError={() => {}}
      />
    </div>
  );
};

export default StepProtocols;
